import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Mail, Phone, Building2, MessageCircle } from "lucide-react";
import { api, WA } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

const badgeColor = { new: "bg-amber-100 text-amber-800", contacted: "bg-blue-100 text-blue-800", closed: "bg-maroon-100 text-maroon-800" };

const AdminLeads = () => {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const { settings } = useSettings();

  const load = () => api.get("/leads").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.put(`/leads/${id}`, null, { params: { status } });
    toast.success("Status diperbarui"); load();
    setSelected((s) => s && s.id === id ? { ...s, status } : s);
  };
  const del = async (id) => { if (window.confirm("Hapus?")) { await api.delete(`/leads/${id}`); load(); setSelected(null); } };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-display font-bold">Leads / Kontak</h1>
        <p className="mt-1 text-sm text-zinc-500">Semua pesan dari form kontak akan muncul di sini.</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-zinc-200 overflow-hidden">
          <div className="divide-y divide-zinc-100 max-h-[75vh] overflow-y-auto">
            {items.map((l) => (
              <button key={l.id} onClick={() => setSelected(l)} className={`w-full text-left p-4 hover:bg-zinc-50 ${selected?.id === l.id ? "bg-maroon-50" : ""}`}>
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-zinc-900">{l.name}</div>
                  <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full font-bold ${badgeColor[l.status] || "bg-zinc-100 text-zinc-600"}`}>{l.status}</span>
                </div>
                <div className="text-xs text-zinc-500 mt-1 truncate">{l.message}</div>
                <div className="text-[10px] text-zinc-400 mt-1">{new Date(l.created_at).toLocaleString("id-ID")}</div>
              </button>
            ))}
            {items.length === 0 && <div className="p-10 text-center text-zinc-500 text-sm">Belum ada leads.</div>}
          </div>
        </div>

        <div className="lg:col-span-3 bg-white rounded-2xl border border-zinc-200 p-6 min-h-[400px]">
          {selected ? (
            <div>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-maroon-700 font-bold">Dari {selected.source}</div>
                  <h2 className="mt-2 text-2xl font-display font-bold">{selected.name}</h2>
                  <div className="mt-1 text-sm text-zinc-500">{new Date(selected.created_at).toLocaleString("id-ID")}</div>
                </div>
                <button onClick={() => del(selected.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-600"><Trash2 size={16} /></button>
              </div>
              <div className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
                {selected.email && <div className="flex items-center gap-2"><Mail size={14} className="text-maroon-700" /> {selected.email}</div>}
                {selected.phone && <div className="flex items-center gap-2"><Phone size={14} className="text-maroon-700" /> {selected.phone}</div>}
                {selected.company && <div className="flex items-center gap-2"><Building2 size={14} className="text-maroon-700" /> {selected.company}</div>}
              </div>
              {selected.product_interest && (
                <div className="mt-4 text-sm">
                  <span className="font-semibold">Rencana Produk:</span> {selected.product_interest}
                </div>
              )}
              <div className="mt-6 p-4 rounded-xl bg-zinc-50 text-zinc-700 whitespace-pre-wrap">{selected.message}</div>
              <div className="mt-6 flex flex-wrap gap-2">
                {selected.phone && (
                  <a
                    href={WA(
                      selected.phone,
                      `Halo ${selected.name}, terima kasih sudah menghubungi ${settings?.site_name || "Mandala"}. Kami ingin menindaklanjuti pesan Anda${selected.product_interest ? ` mengenai "${selected.product_interest}"` : ""}. Apakah ada waktu untuk berdiskusi?`
                    )}
                    target="_blank" rel="noopener noreferrer"
                    onClick={async () => { if (selected.status === "new") await setStatus(selected.id, "contacted"); }}
                    data-testid="lead-wa-chat"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#25D366] text-white text-sm font-semibold hover:bg-[#128C7E] transition">
                    <MessageCircle size={14} /> Chat via WhatsApp
                  </a>
                )}
                {selected.email && (
                  <a href={`mailto:${selected.email}?subject=Re: Konsultasi Maklon Minuman - ${settings?.site_name || "Mandala"}`}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-900 text-white text-sm font-semibold hover:bg-zinc-700 transition">
                    <Mail size={14} /> Reply Email
                  </a>
                )}
              </div>
              <div className="mt-4 flex gap-2 flex-wrap">
                <span className="text-xs text-zinc-500 self-center mr-1">Status:</span>
                <button onClick={() => setStatus(selected.id, "new")} className="px-4 py-2 rounded-full bg-amber-100 text-amber-800 text-sm font-semibold">Baru</button>
                <button onClick={() => setStatus(selected.id, "contacted")} className="px-4 py-2 rounded-full bg-blue-100 text-blue-800 text-sm font-semibold">Dihubungi</button>
                <button onClick={() => setStatus(selected.id, "closed")} className="px-4 py-2 rounded-full bg-maroon-100 text-maroon-800 text-sm font-semibold">Selesai</button>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-zinc-400 text-sm">Pilih lead untuk melihat detail</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLeads;
